import {
  LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts'
import StatsCard from '../../components/StatsCard'
import { mockAnalyticsData, mockAIUsageData, mockFeatureAdoptionFunnel, mockAcquisitionData, mockRetentionCohort } from '../../data/mockData'
import { TrendingUp, Users, Zap, Target } from 'lucide-react'

const COLORS = ['#0d6e6e', '#f5a623', '#14b8a6', '#e94b3c']

export default function AnalyticsPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Analytics</h1>
        <p className="text-pawssible-slate-600 mt-1">Comprehensive user behavior & performance metrics</p>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Avg Session Duration"
          value="12m 34s"
          icon={Zap}
          color="teal"
        />
        <StatsCard
          label="Bounce Rate"
          value="18.5%"
          icon={TrendingUp}
          color="amber"
        />
        <StatsCard
          label="New Users (30d)"
          value="1,243"
          icon={Users}
          color="teal"
        />
        <StatsCard
          label="App Crash Rate"
          value="0.02%"
          icon={Target}
          color="amber"
        />
      </div>

      {/* Chart 1: User Acquisition Over Time */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">User Acquisition by Source (Last 4 Weeks)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={mockAcquisitionData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="week" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
            <Legend />
            <Bar dataKey="appStore" fill="#0d6e6e" name="App Store" radius={[8, 8, 0, 0]} />
            <Bar dataKey="ads" fill="#f5a623" name="Ads" radius={[8, 8, 0, 0]} />
            <Bar dataKey="organic" fill="#14b8a6" name="Organic" radius={[8, 8, 0, 0]} />
            <Bar dataKey="referral" fill="#e94b3c" name="Referral" radius={[8, 8, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Chart 2: Retention Curve (Cohort Analysis) */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">User Retention Curve (Cohort Analysis)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={mockRetentionCohort}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="cohort" stroke="#64748b" />
            <YAxis stroke="#64748b" label={{ value: 'Retention %', angle: -90, position: 'insideLeft' }} />
            <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
            <Legend />
            <Line type="monotone" dataKey="day1" stroke="#0d6e6e" name="Day 1" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="day7" stroke="#14b8a6" name="Day 7" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="day14" stroke="#f5a623" name="Day 14" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="day30" stroke="#e94b3c" name="Day 30" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Chart 3: AI Chat Usage */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">AI Chat Queries & Response Time</h3>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={mockAIUsageData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#64748b" />
            <YAxis yAxisId="left" stroke="#64748b" label={{ value: 'Queries', angle: -90, position: 'insideLeft' }} />
            <YAxis yAxisId="right" orientation="right" stroke="#64748b" label={{ value: 'Response Time (ms)', angle: 90, position: 'insideRight' }} />
            <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
            <Legend />
            <Bar yAxisId="left" dataKey="queries" fill="#0d6e6e" name="Daily Queries" radius={[8, 8, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="avgResponseTime" stroke="#f5a623" name="Avg Response Time (ms)" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Chart 4: Feature Adoption Funnel */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Feature Adoption Funnel</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={mockFeatureAdoptionFunnel}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 200, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" stroke="#64748b" />
            <YAxis dataKey="stage" type="category" stroke="#64748b" width={190} />
            <Tooltip
              contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
              formatter={(value, name) => {
                if (name === 'users') return [value.toLocaleString(), 'Users']
                if (name === 'percentage') return [value.toFixed(1) + '%', 'Adoption %']
                return value
              }}
            />
            <Bar dataKey="users" fill="#0d6e6e" radius={[0, 8, 8, 0]}>
              {mockFeatureAdoptionFunnel.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Engagement Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
              <span className="text-pawssible-slate-600">Daily Active Users (DAU)</span>
              <span className="font-semibold text-pawssible-slate-900">892</span>
            </div>
            <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
              <span className="text-pawssible-slate-600">Weekly Active Users (WAU)</span>
              <span className="font-semibold text-pawssible-slate-900">1,245</span>
            </div>
            <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
              <span className="text-pawssible-slate-600">Monthly Active Users (MAU)</span>
              <span className="font-semibold text-pawssible-slate-900">1,890</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-pawssible-slate-600">DAU/MAU Ratio</span>
              <span className="font-semibold text-pawssible-slate-900">47.2%</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Conversion Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
              <span className="text-pawssible-slate-600">Free → Plus Conversion</span>
              <span className="font-semibold text-pawssible-slate-900">24.3%</span>
            </div>
            <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
              <span className="text-pawssible-slate-600">Free → Family Conversion</span>
              <span className="font-semibold text-pawssible-slate-900">14.2%</span>
            </div>
            <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
              <span className="text-pawssible-slate-600">Total Paid Conversion Rate</span>
              <span className="font-semibold text-pawssible-slate-900">33.8%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-pawssible-slate-600">Trial-to-Paid Conversion</span>
              <span className="font-semibold text-pawssible-slate-900">28.5%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Usage Heatmap */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Feature Usage by Day of Week</h3>
        <div className="grid grid-cols-7 gap-2 text-center">
          {[
            { day: 'Mon', healthLog: 92, aiChat: 87, vaccination: 78, reminder: 95 },
            { day: 'Tue', healthLog: 88, aiChat: 85, vaccination: 75, reminder: 92 },
            { day: 'Wed', healthLog: 95, aiChat: 89, vaccination: 81, reminder: 98 },
            { day: 'Thu', healthLog: 91, aiChat: 86, vaccination: 79, reminder: 94 },
            { day: 'Fri', healthLog: 89, aiChat: 88, vaccination: 82, reminder: 96 },
            { day: 'Sat', healthLog: 94, aiChat: 91, vaccination: 85, reminder: 97 },
            { day: 'Sun', healthLog: 96, aiChat: 93, vaccination: 88, reminder: 99 },
          ].map((row, i) => (
            <div key={i} className="space-y-2">
              <div className="font-semibold text-pawssible-slate-900">{row.day}</div>
              <div className="text-xs text-pawssible-slate-600">Health Log: {row.healthLog}%</div>
              <div className="text-xs text-pawssible-slate-600">AI Chat: {row.aiChat}%</div>
              <div className="text-xs text-pawssible-slate-600">Vaccination: {row.vaccination}%</div>
              <div className="text-xs text-pawssible-slate-600">Reminder: {row.reminder}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
